import { Link } from "react-router-dom";
import { PageHeader } from "@/shared/components/PageHeader";
import { Button } from "@/shared/components/ui/button";
import { StatusText } from "@/shared/components/ui/status-chip";
import { TableCard, TableHeadRow, TableRow, Th, Empty } from "@/shared/components/ui/table";
import { EmptyState } from "@/shared/components/ui/empty-state";
import { useAllSequences } from "./use-global";

const GRID = "grid-cols-[2fr_2fr_1fr_1fr_1fr_1fr]";

/** Sequences (global nav) — every outreach sequence across campaigns. */
export function SequencesPage() {
  const { data, isLoading, isError, refetch } = useAllSequences();
  const rows = data ?? [];

  if (isError) {
    return (
      <div>
        <PageHeader title="Sequences" />
        <EmptyState
          message="Couldn't load sequences."
          action={
            <Button variant="outline" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }
  if (!isLoading && rows.length === 0) {
    return (
      <div>
        <PageHeader title="Sequences" />
        <EmptyState message="No sequences yet. Build and launch one from a campaign's Sequences tab." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Sequences"
        footnote="Every outreach sequence across your campaigns. Sequences are built and launched inside a campaign."
      />
      <TableCard>
        <TableHeadRow grid={GRID}>
          <Th>Name</Th>
          <Th>Campaign</Th>
          <Th>Status</Th>
          <Th>Channel</Th>
          <Th>Steps</Th>
          <Th>Enrolled</Th>
        </TableHeadRow>
        {isLoading ? (
          <SkeletonRows />
        ) : (
          rows.map((s) => (
            <TableRow key={s.id} grid={GRID} data-testid={`seq-row-${s.id}`}>
              <div className="font-sans text-[13.5px] font-semibold text-ge-primary">{s.name}</div>
              <Link
                to={`/campaigns/${s.campaignId}/sequences`}
                className="font-mono text-[11.5px] text-ge-secondary hover:text-ge-accent"
              >
                {s.campaignName ?? s.campaignId}
              </Link>
              <div>
                <StatusText tone={s.status === "LAUNCHED" ? "accent" : "faint"}>{s.status}</StatusText>
              </div>
              <div className="font-mono text-[11.5px] text-ge-value">{s.channel}</div>
              <div className="font-mono text-[12px] text-ge-value">{s.steps || <Empty />}</div>
              <div className="font-mono text-[12px] text-ge-value">{s.enrolled || <Empty />}</div>
            </TableRow>
          ))
        )}
      </TableCard>
    </div>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 4 }).map((_, i) => (
        <TableRow key={i} grid={GRID} aria-hidden data-testid="seq-skeleton">
          <div className="h-[14px] w-2/3 animate-pulse rounded bg-ge-border-card" />
          {Array.from({ length: 5 }).map((__, j) => (
            <div key={j} className="h-[12px] w-10 animate-pulse rounded bg-ge-border-card" />
          ))}
        </TableRow>
      ))}
    </>
  );
}
